/**
 * ContextPane primitives - the vocabulary of a surface context pane.
 *
 * Every detail surface (matter, project, task, contact, review) docks a narrow
 * left pane holding the facts of the record. These are the parts that pane is
 * built from, lifted VERBATIM out of `Matters/surface/MatterContextPane` - the
 * pane that got them right first - so the rest of the app can read the same
 * way instead of each pane re-deriving a slightly different 10px label.
 *
 * They are deliberately dumb: no data fetching, no i18n, no store. A pane
 * passes already-resolved strings in and lays them out.
 *
 *   ContextSection  a section heading (10px, uppercase, tracked, muted) with
 *                   an optional inline action on the right
 *   Chip            one fact, pill-shaped, wraps with its siblings
 *   StatGrid        auto-fit grid of tiles - columns follow the PANE's width
 *   StatTile        a pressable count: value over label, navigates on click
 *   TimelineRail    the vertical hairline the timeline dots sit on
 *   TimelineRow     one stop on it - dot, label, value ("—" when not set)
 *   PersonRow       an initial-avatar, a name, and a role/side label
 */

import React from 'react';
import clsx from 'clsx';

/** A section heading, with an optional action (link, button) on the right. */
export const ContextSection: React.FC<{
  children: React.ReactNode;
  action?: React.ReactNode;
}> = ({ children, action }) => (
  <div className="flex items-center justify-between gap-2 mb-1.5">
    <h4 className="text-[10px] font-semibold uppercase tracking-wider text-fg-muted">
      {children}
    </h4>
    {action}
  </div>
);

/** A fact chip: icon or flag, then the value. Wraps with its siblings. */
export const Chip: React.FC<{
  icon?: React.ReactNode;
  children: React.ReactNode;
  tone?: 'default' | 'accent';
  title?: string;
}> = ({ icon, children, tone = 'default', title }) => (
  <span
    title={title}
    className={clsx(
      'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5',
      'text-[11px] font-medium whitespace-nowrap max-w-full',
      tone === 'accent'
        ? 'border-champagne/40 bg-champagne/10 text-champagne'
        : 'border-hairline-soft bg-surface-sunken/70 text-fg-body'
    )}
  >
    {icon && <span className="[&>svg]:h-3 [&>svg]:w-3 flex-shrink-0">{icon}</span>}
    <span className="truncate">{children}</span>
  </span>
);

/**
 * The tile grid.
 *
 * auto-fit + minmax, not a fixed column count: a context pane resizes from
 * 240 to 720, so the number of columns has to follow the PANE's width, which
 * a viewport breakpoint cannot see.
 */
export const StatGrid: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div
    className={clsx('grid divide-x divide-y divide-hairline-soft', className)}
    style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(7rem, 1fr))' }}
  >
    {children}
  </div>
);

/** A count tile that navigates. Same shape as `KpiTile`, but pressable. */
export const StatTile: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number;
  onClick?: () => void;
}> = ({ icon, label, value, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    data-testid={`context-stat-${label}`}
    className={clsx(
      'flex items-center gap-2.5 p-3 text-start w-full',
      'transition-colors duration-200 ease-slide',
      onClick && 'hover:bg-surface/70 cursor-pointer'
    )}
  >
    <span className="[&>svg]:h-4 [&>svg]:w-4 text-fg-muted flex-shrink-0">{icon}</span>
    <span className="min-w-0">
      <span className="block text-base font-semibold tabular-nums text-fg-strong leading-none">
        {value}
      </span>
      <span className="block text-[10px] text-fg-muted truncate mt-0.5">{label}</span>
    </span>
  </button>
);

/** The rule the timeline dots sit on. Wraps a run of `TimelineRow`s. */
export const TimelineRail: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="relative">
    <span aria-hidden className="absolute start-[3px] top-2 bottom-2 w-px bg-hairline-soft" />
    {children}
  </div>
);

/** One stop on the vertical timeline. A missing value reads muted, not absent. */
export const TimelineRow: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
  <div className="relative flex items-baseline gap-3 ps-4 py-1">
    <span
      aria-hidden
      className={clsx(
        'absolute start-0 top-[0.55rem] h-1.5 w-1.5 rounded-full',
        value ? 'bg-champagne' : 'bg-fg-muted/30'
      )}
    />
    <span className="text-[11px] text-fg-muted w-24 flex-shrink-0">{label}</span>
    <span className={clsx('text-xs', value ? 'text-fg-body' : 'text-fg-muted')}>
      {value || '—'}
    </span>
  </div>
);

/**
 * A person or party.
 *
 * A rounded initial rather than a `ProfileImage`: the parties on a matter are
 * organizations and individuals on the other side of a deal, not platform
 * users - there is no account, no avatar and no email to resolve, so the
 * person-shaped component would only ever render its fallback.
 */
export const PersonRow: React.FC<{
  name: string;
  /** Optional qualifier appended to the label - "Client · Buyer". */
  role?: string;
  /** The muted uppercase line above the name. */
  sideLabel: string;
  /** Avatar treatment: `accent` for our side, `info` for the other. */
  tone?: 'accent' | 'info';
}> = ({ name, role, sideLabel, tone = 'accent' }) => {
  const initial = (name || '?').trim().charAt(0).toUpperCase() || '?';
  return (
    <div className="flex items-center gap-2.5 py-2">
      <span
        aria-hidden
        className={clsx(
          'h-7 w-7 rounded-lg flex items-center justify-center flex-shrink-0',
          'text-[11px] font-semibold',
          tone === 'accent'
            ? 'bg-champagne/15 text-champagne border border-champagne/30'
            : 'bg-info-50 dark:bg-info-900/20 text-info-700 dark:text-info-300 border border-info-200 dark:border-info-800/60'
        )}
      >
        {initial}
      </span>
      <div className="min-w-0">
        <p className="text-[10px] font-medium uppercase tracking-wider text-fg-muted truncate">
          {sideLabel}{role ? ` · ${role}` : ''}
        </p>
        <p className="text-sm text-fg-body truncate">{name}</p>
      </div>
    </div>
  );
};
